import isNil from 'lodash.isnil';
import get from 'lodash.get';
import MARKS_IN_TEXT from '../../constants/reading/ProbeDetailsConstants';
import { TEXT_LEVELS_MAPPER } from '../../constants/reading/TroConstants';

const { PAGE_START, PAGE_END, ITALIC_START, ITALIC_END, TEXT, BREAK, PARAGRAPH_END } =
    MARKS_IN_TEXT;

const getPrefixOptions = prefixes => {
    let isNewPage = false;
    let isItalicStart = false;
    let textPrefix = '';

    if (!isNil(prefixes)) {
        isNewPage = prefixes.some(({ type }) => type === PAGE_START);
        isItalicStart = prefixes.some(({ type }) => type === ITALIC_START);
        textPrefix = get(
            prefixes.find(({ type }) => type === TEXT),
            'value',
            textPrefix,
        );
    }

    return {
        isNewPage,
        isItalicStart,
        textPrefix,
    };
};

const getSuffixOptions = suffixes => {
    let isItalicEnd = false;
    let hasNewLine = false;
    let textSuffix = '';

    if (!isNil(suffixes)) {
        isItalicEnd = suffixes.some(({ type }) => type === ITALIC_END);
        hasNewLine =
            suffixes.some(({ type }) => type === BREAK || type === PARAGRAPH_END) &&
            isNil(suffixes.find(({ type }) => type === PAGE_END));
        textSuffix = get(
            suffixes.find(({ type }) => type === TEXT),
            'value',
            textSuffix,
        );
    }

    return {
        isItalicEnd,
        hasNewLine,
        textSuffix,
    };
};

const parseWords = words => {
    let numberPage = 0;
    let isItalic = false;
    let isNewLine = false;

    return words.map(({ prefix, suffix, word }, number) => {
        const { textPrefix, isNewPage, isItalicStart } = getPrefixOptions(prefix);
        const { hasNewLine, isItalicEnd, textSuffix } = getSuffixOptions(suffix);

        if (isNewPage) {
            numberPage += 1;
        }

        if (isItalicStart) {
            isItalic = true;
        }

        const currentWordOptions = {
            number,
            word: `${textPrefix}${word}${textSuffix}`,
            isNewLine,
            isNewPage,
            isItalic,
            numberPage,
        };

        /** We should rewrite params for the next word,
         * that why we rewrite isNewLine && isItalic params
         * after currentWordOptions initialization
         * FE: [{ type:italic_start }, { } , { type:italic_end }] */
        isNewLine = hasNewLine;

        if (isItalicEnd) {
            isItalic = false;
        }

        return currentWordOptions;
    });
};

const processAnswers = answers =>
    answers.map(({ response_idx: id, text, is_correct: isCorrect }) => ({ id, text, isCorrect }));

const parseBookData = response => {
    const questions = response.comprehension_items.map(
        ({ item_idx: id, item_content: { question, responses } }) => ({
            id,
            text: question,
            answers: processAnswers(responses),
        }),
    );

    return {
        [response.book_id]: {
            questions,
            words: parseWords(response.content),
        },
    };
};

export const parseAllBooksData = response => {
    return response.reduce(
        (
            acc,
            {
                period,
                grade_level: grade,
                relative_level: relativeLevel,
                reading_level: readingLevel,
                thresholds,
                title,
            },
        ) => {
            acc[period] = {
                ...acc[period],
                [grade]: {
                    ...acc[period]?.[grade],
                    thresholds: {
                        fluency: thresholds.fluency.toString(),
                        comprehension: {
                            ...acc[period]?.[grade]?.thresholds?.comprehension,
                            [TEXT_LEVELS_MAPPER[relativeLevel]]:
                                thresholds.comprehension.toString(),
                        },
                    },
                    booksInfo: {
                        ...acc[period]?.[grade]?.booksInfo,
                        [TEXT_LEVELS_MAPPER[relativeLevel]]: {
                            bookTitle: title,
                            readingLevel,
                        },
                    },
                },
            };

            return acc;
        },
        {},
    );
};

export default parseBookData;
